import React, { useState } from "react";
import "./kiosk.scss";
import { KioskHome } from "./KioskHome";
import { KioskConfirmDetails } from "./KioskConfirmDetails";
import { KioskSuccess } from "./KioskSuccess";
import { KioskNotFound } from "./KioskNotFound";
import { KioskAlert } from "./KioskAlert";
import { GetKioskBookingLookup } from "api/rpc/2024-04/facilityAdmin/kiosk/booking";
import { StatusCode } from "api/protocols";
import { IBooking } from "redux/reducers/models/teetime";
import { ICustomer } from "redux/reducers/models/customer";
import { PutBookingParticipant } from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import moment from "moment";
import { KioskScanQrCode } from "./KioskScanQrCode";
import { KioskSelectPlayer } from "./KioskSelectPlayer";

type KioskStep =
  | "HOME"
  | "SCAN_QR"
  | "SELECT_PLAYER"
  | "CONFIRM_DETAILS"
  | "SUCCESS"
  | "NOT_FOUND"
  | "PAYMENT_REQUIRED"
  | "WINDOW_CLOSED";

export default function Kiosk() {
  const dispatch = useAppDispatch();
  const [currentStep, setCurrentStep] = useState<KioskStep>("HOME");
  const [bookingReference, setBookingReference] = useState("");
  const [foundBooking, setFoundBooking] = useState<IBooking | null>(null);
  const [foundCustomer, setFoundCustomer] = useState<Partial<ICustomer> | null>(null);
  const [searchType, setSearchType] = useState<string>("");
  const [cameFromSelectPlayer, setCameFromSelectPlayer] = useState(false);

  const goToStep = (step: KioskStep) => {
    if (step !== "CONFIRM_DETAILS") {
      setCameFromSelectPlayer(false);
    }
    setCurrentStep(step);
  };

  const resetFlow = () => {
    setBookingReference("");
    setFoundBooking(null);
    setFoundCustomer(null);
    setSearchType("");
    setCameFromSelectPlayer(false);
    setCurrentStep("HOME");
  };

  const handleFindBooking = () => {
    void findBooking();
  };

  const findBooking = async (val?: string) => {
    const booking_reference = (val || bookingReference).trim();
    if (!booking_reference) {
      return;
    }

    const lookupRes = await GetKioskBookingLookup({ booking_reference }, true);

    if (lookupRes.status === StatusCode.UNPROCESSABLE_ENTITY && !lookupRes.data) {
      setFoundBooking(null);
      setCurrentStep("WINDOW_CLOSED");
      return;
    }

    if (lookupRes.status === StatusCode.NOT_FOUND || !lookupRes.data) {
      setFoundBooking(null);
      setCurrentStep("NOT_FOUND");
      return;
    }

    const booking = lookupRes?.data?.booking;
    const customer = lookupRes?.data?.customer;
    const search_type = lookupRes?.data?.search_type || "";

    setFoundBooking(booking);
    setFoundCustomer(customer);
    setSearchType(search_type);

    const hasMultipleParticipants = booking?.participants?.length > 1;
    if (search_type === "confirmation_number" && hasMultipleParticipants) {
      goToStep("SELECT_PLAYER");
      return;
    }

    goToStep("CONFIRM_DETAILS");
  };

  const handleConfirmCheckIn = async () => {
    const participant = foundBooking?.participants?.find(
      p => p.customer_id === foundCustomer?.id,
    );
    const timePeriod = foundBooking?.tee_time?.start_time
      ? moment(foundBooking?.tee_time?.start_time, "HH:mm:ss").format("A")
      : "";

    if (!participant) {
      dispatch(showError("Participant not found"));
      return;
    }

    if (participant?.order_financial_status === null && participant?.green_fee_price > 0) {
      goToStep("PAYMENT_REQUIRED");
      return;
    }

    if (participant?.check_in_status === "checked") {
      dispatch(
        showSuccess(
          `You've already checked-in for tee time ${foundBooking?.tee_time?.start_time} ${timePeriod}. Please proceed to your first tee`,
        ),
      );
      return;
    }

    const checkInRes = await PutBookingParticipant(
      {
        id: participant.id,
        check_in_status: "checked",
      },
      true,
    );

    if (checkInRes.status !== StatusCode.OK) {
      dispatch(showError((checkInRes.data as string) || "Error checking in"));
      return;
    }

    goToStep("SUCCESS");
  };

  const handleBackFromConfirmDetails = () => {
    if (cameFromSelectPlayer) {
      setCurrentStep("SELECT_PLAYER");
      return;
    }

    resetFlow();
  };

  const renderContent = () => {
    switch (currentStep) {
      case "HOME":
      case "SCAN_QR":
        return (
          <KioskHome
            bookingReference={bookingReference}
            setBookingReference={setBookingReference}
            onFindBooking={handleFindBooking}
            onScanQR={() => goToStep("SCAN_QR")}
          />
        );
      case "SELECT_PLAYER":
        return (
          <KioskSelectPlayer
            booking={foundBooking}
            customer={foundCustomer}
            onSelectCustomer={customer => {
              setFoundCustomer(customer);
            }}
            onContinue={() => {
              setCameFromSelectPlayer(true);
              goToStep("CONFIRM_DETAILS");
            }}
            onBack={resetFlow}
          />
        );
      case "CONFIRM_DETAILS":
        return (
          <KioskConfirmDetails
            booking={foundBooking}
            customer={foundCustomer}
            searchType={searchType}
            setCustomer={setFoundCustomer}
            onConfirm={handleConfirmCheckIn}
            onCancel={handleBackFromConfirmDetails}
            onTimeout={resetFlow}
          />
        );
      case "SUCCESS":
        return (
          <KioskSuccess booking={foundBooking} customer={foundCustomer} searchType={searchType} onDone={resetFlow} />
        );
      case "NOT_FOUND":
        return <KioskNotFound onBack={resetFlow} />;
      case "PAYMENT_REQUIRED":
        return (
          <KioskAlert
            title="Booking amount due"
            subtitles={[
              "Payment is required to complete check-in",
              "Please go to the Pro Shop to pay your remaining amount.",
            ]}
            onBack={resetFlow}
          />
        );
      case "WINDOW_CLOSED":
        return (
          <KioskAlert
            title="Please visit the Pro Shop"
            subtitles={["The check-in window has closed.", "Please visit the Pro Shop for assistance."]}
            onBack={resetFlow}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="ui-kiosk">
      {renderContent()}
      {currentStep === "SCAN_QR" && (
        <div className="ui-kiosk-overlay">
          <KioskScanQrCode
            onConfirm={val => {
              setBookingReference(val);
              void findBooking(val);
            }}
            onCancel={resetFlow}
          />
        </div>
      )}
    </div>
  );
}
